import { useState } from "react";
import { useTranslation } from "react-i18next";
import { themes } from "@/data/photographers";
import type { CreatorSelectedImage } from "@/lib/community-submissions";
import { PhotoLightbox, type LightboxInfo } from "@/components/PhotoLightbox";

type Group = { key: string; label: string; items: CreatorSelectedImage[] };

export function CreatorSelectedSection({ items, name, creatorType = "photographer" }: { items: CreatorSelectedImage[]; name: string; creatorType?: "photographer" | "visagist" }) {
  const { t } = useTranslation();
  const [lightbox, setLightbox] = useState<{ items: LightboxInfo[]; index: number } | null>(null);

  if (!items || items.length === 0) return null;

  const community = items.filter((it) => it.source === "community");
  const curated = items.filter((it) => it.source === "curated");

  const groups: Group[] = [];
  for (const theme of themes) {
    const inTheme = community.filter((it) => (it.theme ?? "open") === theme.id);
    if (inTheme.length > 0) groups.push({ key: theme.id, label: theme.label, items: inTheme });
  }
  if (curated.length > 0) {
    groups.push({ key: "__curated", label: t("profile.curatedSelection"), items: curated });
  }

  if (groups.length === 0) return null;

  const makeInfo = (it: CreatorSelectedImage): LightboxInfo => ({
    image: it.image_url,
    caption: it.caption ?? null,
    photographerName: name,
    photographerId: null,
    region: null,
    theme: themes.find((th) => th.id === (it.theme ?? "open"))?.label ?? null,
    creatorType,
  });
  const flatItems = groups.flatMap((g) => g.items);

  return (
    <section className="border-b border-border">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-20">
        <h2 className="eyebrow text-muted-foreground mb-8">{t("profile.selectedSection")}</h2>
        <div className="space-y-12">
          {groups.map((g) => (
            <div key={g.key}>
              <div className="flex items-baseline justify-between mb-4 border-b border-border pb-3">
                <h3 className="serif text-3xl md:text-4xl">{g.label}</h3>
                <span className="eyebrow text-muted-foreground">
                  {g.items.length} {g.items.length === 1 ? t("profile.imageSingular") : t("profile.imagePlural")}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 items-start">
                {g.items.map((it) => (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => setLightbox({ items: flatItems.map(makeInfo), index: flatItems.indexOf(it) })}
                    className="block w-full overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground"
                    aria-label={it.caption || name}
                  >
                    <img
                      src={it.image_url}
                      alt={it.caption || `${name}, Selected`}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-auto cursor-zoom-in no-bw bg-muted"
                    />
                    {it.caption && (
                      <span className="block text-sm text-muted-foreground mt-2 leading-relaxed text-left">{it.caption}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <PhotoLightbox
        info={lightbox ? lightbox.items[lightbox.index] : null}
        onClose={() => setLightbox(null)}
        onPrev={lightbox && lightbox.index > 0 ? () => setLightbox((s) => (s ? { ...s, index: s.index - 1 } : s)) : undefined}
        onNext={lightbox && lightbox.index < lightbox.items.length - 1 ? () => setLightbox((s) => (s ? { ...s, index: s.index + 1 } : s)) : undefined}
        neighborImages={lightbox ? [lightbox.items[lightbox.index - 1]?.image, lightbox.items[lightbox.index + 1]?.image] : undefined}
      />
    </section>
  );
}